import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { Download, RotateCcw, AlertTriangle, Database, Search, FileSearch } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';

export default function OrphanedDataReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/data-report/orphaned-fields');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleExport = async (fieldId, label) => {
    try {
      const res = await api.get(`/admin/data-report/orphaned-fields/${fieldId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orphaned_${label.replace(/[^a-z0-9]/gi, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleRestore = async (fieldId) => {
    try {
      await api.post('/admin/recycle-bin/restore', { type: 'field', id: fieldId });
      toast.success('Field restored');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Restore failed');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  const items = (data?.orphaned_fields || []).filter(o =>
    !search ||
    (o.original_field?.label || '').toLowerCase().includes(search.toLowerCase()) ||
    o.record_type.name.toLowerCase().includes(search.toLowerCase()) ||
    o.field_id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto">
      <AdminPageHeader title="Orphaned Data Report" description="Data from deleted fields that still exists on records" icon={FileSearch} iconColor="text-indigo-600" iconBg="bg-indigo-50" />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Orphaned Fields</p>
          <p className="text-2xl font-bold text-gray-900">{data?.total_orphaned || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Affected Records</p>
          <p className="text-2xl font-bold text-gray-900">{data?.total_affected_records || 0}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <span className="text-xl">✅</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No orphaned data</h3>
          <p className="text-sm text-gray-500">All field data on records has a matching active field definition.</p>
        </div>
      ) : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by field name or record type..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>

          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {item.original_field ? (
                          <>
                            <span className="text-sm font-semibold text-gray-900">{item.original_field.label}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{item.original_field.field_type}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-semibold text-gray-500">Unknown Field</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">permanently deleted</span>
                          </>
                        )}
                        <span className="text-xs text-gray-400">on</span>
                        <span className="text-xs font-medium text-gray-600">{item.record_type.name}</span>
                      </div>
                      <p className="text-xs text-gray-400 font-mono">{item.field_id}</p>
                      {item.original_field?.deleted_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Deleted {new Date(item.original_field.deleted_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                        {item.record_count} record{item.record_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Sample values */}
                  {item.sample_values.length > 0 && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">Sample values:</p>
                      <div className="space-y-1">
                        {item.sample_values.map((sv, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-500 truncate max-w-[200px]">{sv.record_title}</span>
                            <span className="text-gray-300">→</span>
                            <span className="text-gray-700 font-mono truncate max-w-[300px]">
                              {typeof sv.value === 'object' ? JSON.stringify(sv.value) : String(sv.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
                  <button onClick={() => handleExport(item.field_id, item.original_field?.label || 'unknown')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors">
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </button>
                  {item.restorable && (
                    <button onClick={() => handleRestore(item.field_id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 transition-colors">
                      <RotateCcw className="h-3.5 w-3.5" /> Restore Field
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
